import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";

import { fetchOrderEvents } from "../../features/orders/api/ordersApi";
import { updateOrderCaches, confirmCommand } from "../../sync/command-queue/orderStatusQueue";
import { joinOrderRooms, ordersSocket } from "../../sync/socket/ordersSocket";
import type {
  LegacyItemStatusEvent,
  LegacyOrderStatusEvent,
  LegacyTrackingEvent,
  OrderStatusChangedEvent,
} from "../../shared/api/types";
import { getSetting, setSetting } from "../../storage/indexed-db/database";
import { recordDuration } from "../../shared/utilities/performance";

export function useOrderEvents(
  queryClient: QueryClient,
  enabled: boolean,
  visibleOrderIds: number[],
  baselineEventCursor: number,
  recoverSnapshot: () => void,
): void {
  useEffect(() => {
    if (!enabled) return;

    const handleEvent = async (event: OrderStatusChangedEvent) => {
      const lastEventId = (await getSetting<number>("orders-event-cursor")) ?? 0;
      if (!event?.eventId || event.eventId <= lastEventId) return;
      const started = performance.now();
      updateOrderCaches(
        queryClient,
        event.entityId,
        (order) => ({
          ...order,
          status: event.payload.canonicalStatus,
          entityVersion: event.entityVersion,
          syncState: undefined,
        }),
        (order) => ({
          ...order,
          status: event.payload.canonicalStatus,
          entityVersion: event.entityVersion,
          syncState: undefined,
        }),
      );
      await Promise.all([
        confirmCommand(event.clientMutationId),
        setSetting("orders-event-cursor", event.eventId),
      ]);
      recordDuration("erp.order-status.confirmed", started);
    };

    const handleLegacyStatus = (event: LegacyOrderStatusEvent) => {
      const orderId = Number(event.order_id);
      if (!orderId || !event.new_status) return;
      updateOrderCaches(
        queryClient,
        orderId,
        (order) => ({ ...order, status: event.new_status }),
        (order) => ({ ...order, status: event.new_status }),
      );
    };

    const handleLegacyItem = (event: LegacyItemStatusEvent) => {
      const orderId = Number(event.order_id);
      const itemId = Number(event.item_id);
      const percent = Number(event.status_percent);
      if (!orderId || !itemId || !Number.isFinite(percent)) return;
      updateOrderCaches(
        queryClient,
        orderId,
        (order) => order,
        (order) => ({
          ...order,
          items: order.items.map((item) =>
            item.id === itemId
              ? { ...item, statusPercent: Math.max(0, Math.min(100, percent)) }
              : item,
          ),
        }),
      );
    };

    const handleLegacyTracking = (event: LegacyTrackingEvent) => {
      const orderId = Number(event.order_id);
      if (!orderId) return;
      const trackingNumber = event.tracking_number || null;
      updateOrderCaches(
        queryClient,
        orderId,
        (order) => ({ ...order, trackingNumber }),
        (order) => ({ ...order, trackingNumber }),
      );
    };

    const catchUp = async () => {
      const stored = await getSetting<number>("orders-event-cursor");
      let cursor = stored ?? baselineEventCursor;
      if (stored === undefined) {
        await setSetting("orders-event-cursor", cursor);
      }
      for (let page = 0; page < 20; page += 1) {
        const missed = await fetchOrderEvents(cursor);
        for (const event of missed.items) await handleEvent(event);
        if (missed.items.length === 0 || missed.lastEventId <= cursor) return;
        cursor = missed.lastEventId;
        if (missed.items.length < 200) return;
      }
      // More than 4000 missed events is exceptional. One bulk snapshot is
      // safer than continuing an unbounded replay.
      recoverSnapshot();
    };

    const onConnect = async () => {
      joinOrderRooms(visibleOrderIds);
      try {
        await catchUp();
      } catch {
        // A socket ettől még él; a következő reconnect ismét megpróbálja a catch-upot.
        recoverSnapshot();
      }
    };
    const onReconnect = () => recoverSnapshot();
    ordersSocket.on("connect", onConnect);
    ordersSocket.on("order.statusChanged", handleEvent);
    ordersSocket.on("order_status_update", handleLegacyStatus);
    ordersSocket.on("item_status_update", handleLegacyItem);
    ordersSocket.on("tracking_update", handleLegacyTracking);
    ordersSocket.io.on("reconnect", onReconnect);
    ordersSocket.connect();
    return () => {
      ordersSocket.off("connect", onConnect);
      ordersSocket.off("order.statusChanged", handleEvent);
      ordersSocket.off("order_status_update", handleLegacyStatus);
      ordersSocket.off("item_status_update", handleLegacyItem);
      ordersSocket.off("tracking_update", handleLegacyTracking);
      ordersSocket.io.off("reconnect", onReconnect);
    };
  }, [
    baselineEventCursor,
    enabled,
    queryClient,
    recoverSnapshot,
    visibleOrderIds,
  ]);

  useEffect(() => {
    joinOrderRooms(visibleOrderIds);
  }, [visibleOrderIds]);
}
